
import u from "umbrellajs"
import $ from "jquery"
import serialize from "form-serialize"
import _omit from "lodash/omit"
import _compact from "lodash/compact"
import _once from "lodash/once"
import _merge from "lodash/merge"
import _cloneDeep from "lodash/cloneDeep"

import NumbersFormatter from "app/numbers/formatter/index"
import NumberOfPerformedListingSearchAfterPageLoad from "app/tracking/states/number_of_performed_listing_search_after_page_load/index"

import FilterManager from "./manager"

const CookiesKeys = {
  SAVED: "window.App.Cells.ListingFilter.SAVED",
}


const getFormElement = _once(() => null)

// Only for in memory, values like array
// They won't be put inside the form
let in_memory_filter_options = {}


interface FilterOptions {
  travel_time_nodes?: any[],

  removeNeighbourhoodCriteria?: boolean,
  removePlaceCriteria?: boolean,
  [key: string]: any,
}

type ElementFetcher = () => Element | null
let form_element_fetcher: ElementFetcher = getFormElement

interface DefaultListingAttributes {
  building_id?: string | number

  is_village_house?: boolean
}
let default_listing_attributes = null as null | DefaultListingAttributes

interface InitOptions {
  form_element_fetcher: ElementFetcher,
  default_listing_attributes?: DefaultListingAttributes,
}

const Filter = {
  // store the user filter values
  filterOptions: {} as object,

  update: update,

  initialize: (options: InitOptions) => {
    form_element_fetcher = options.form_element_fetcher
    if (options.default_listing_attributes != null) {
      default_listing_attributes = options.default_listing_attributes
    }

    const form_element = form_element_fetcher()
    if (form_element == null) {
      return
    }

    initialFilterControls()
    Filter.update(false)

    // Tracking option update
    u(form_element as HTMLElement).on("change", (() => updateFilterOptionsObject()))
  },

  getFilterOptions: ((options: FilterOptions = {}): {[key: string]: any} => {
    // Parse options
    //
    // To avoid accident, only `true` will really activate
    const removeNeighbourhoodCriteria = options.removeNeighbourhoodCriteria === true
    const removePlaceCriteria = options.removePlaceCriteria === true

    // Copy object to avoid mutation of the value here
    // Implementation recommended from
    // https://stackoverflow.com/questions/122102/what-is-the-most-efficient-way-to-clone-an-object-in-javascript
    let copied_filter_criteria = _cloneDeep(Filter.filterOptions)

    let criteria_keys_to_be_removed = []

    if (removeNeighbourhoodCriteria) {
      // using `omit` since `delete` not supported in some browsers
      criteria_keys_to_be_removed.push("neighbourhood_ids")
    }

    if (removePlaceCriteria) {
      criteria_keys_to_be_removed = criteria_keys_to_be_removed.concat([
        "place_radius",
        "place_position",
        "place_name",
        "place_coordinates",
      ])
    }

    if (criteria_keys_to_be_removed.length > 0) {
      copied_filter_criteria = _omit(copied_filter_criteria, criteria_keys_to_be_removed)
    }

    return copied_filter_criteria
  }),

  updateFilterOptionsInMemory: (new_filter_options: FilterOptions) => {
    const new_filter_opts: FilterOptions = {}

    if (Array.isArray(new_filter_options.travel_time_nodes)) {
      new_filter_opts.travel_time_nodes = new_filter_options.travel_time_nodes
    } else {
      new_filter_opts.travel_time_nodes = []
    }

    // Need to be false to ensure empty array are assigned
    in_memory_filter_options = _merge(
      in_memory_filter_options,
      new_filter_opts,
    )
    // Update in memory options
    // Need to be false to ensure empty array are assigned
    Filter.filterOptions = _merge(
      Filter.filterOptions,
      in_memory_filter_options
    )
  },
}

function updateFilterOptionsObject() {
  const form_element = form_element_fetcher()
  if (form_element == null) {
    return
  }

  const new_filter_opts = (serialize(($(form_element)[0] as HTMLFormElement), {hash: true}).listing_search || {}) as any

  if (new_filter_opts.price_max){
    new_filter_opts.price_max = NumbersFormatter.numberWithoutCommas(new_filter_opts.price_max)
  }
  if (new_filter_opts.price_min){
    new_filter_opts.price_min = NumbersFormatter.numberWithoutCommas(new_filter_opts.price_min)
  }
  if (new_filter_opts.gross_max){
    new_filter_opts.gross_max = NumbersFormatter.numberWithoutCommas(new_filter_opts.gross_max)
  }
  if (new_filter_opts.gross_min){
    new_filter_opts.gross_min = NumbersFormatter.numberWithoutCommas(new_filter_opts.gross_min)
  }

  // We don't want empty neighbourhood ids
  //
  // Array unique function from
  // https://stackoverflow.com/a/14438954/838346
  new_filter_opts.neighbourhood_ids =
    _compact(new_filter_opts.neighbourhood_ids).filter((v, i, a) => a.indexOf(v) === i)
  // Place search param options to be used in JS
  // Search `place_coordinates`
  if (typeof new_filter_opts.place_position === "string") {
    new_filter_opts.place_coordinates = new_filter_opts.place_position.split(",")
  } else {
    // Default value
    new_filter_opts.place_coordinates = []
  }

  if (typeof new_filter_opts.travel_time_position === "string") {
    new_filter_opts.travel_time_coordinates = new_filter_opts.travel_time_position.split(",")
  } else {
    // Default value
    new_filter_opts.travel_time_coordinates = []
  }

  // Need to be false to ensure empty array are assigned
  Filter.filterOptions = _merge(
    new_filter_opts,
    default_listing_attributes,
    in_memory_filter_options,
  )
}

function update(full_update: boolean = true) {
  // We update the `filterOptions` object here, and then call the
  // onFilterUpdate hooks afterward. This will lead to any change
  // to the form in the hooks reflected in the `filterOptions`.
  //
  // If you change the form in the hook, you will have to call
  // `updateFilterOptionsObject` manually by yourself.
  updateFilterOptionsObject()

  if (full_update) {
    FilterManager.emit("filter:update")
  }
}


const updateWithLoading = () => {
  update()
  NumberOfPerformedListingSearchAfterPageLoad.addOne()
}

function initialFilterControls() {
  const form_element = form_element_fetcher()
  if (form_element == null) {
    return
  }

  // Prevent accidental form submission with enter key or other means
  u(form_element as HTMLElement).on("submit", (e) => {
    e.preventDefault()
  })

  FilterManager.on("filter:change:action", () => updateWithLoading())
}

// $(".cell--listings--searches--filter-bar__btn-update-search-result").on "click", ->
//   Filter.updateWithLoading()

//   # This prevents the dropdowns and dialogs from closing. Historially we
//   # have a visible update button which users actually click. The user's
//   # clicking the update button should make any temporary dropdowns close
//   # for sure. But now, the button is hidden. Whenever changes happens,
//   # we trigger this "click" event from somewhere else programmatically. In
//   # this case, because user didn't really "click", we shouldn't let the
//   # dropdowns close.
//   #
//   # This `false` prevents the html/body tag's click event from
//   # happening. As usually the code for closing dropdowns are in html/body
//   # tag. I personally haven't read exactly where all these things happens,
//   # but simply blocking propagation here seems to work just fine.
//   #
//   false

export {
  Filter,
  CookiesKeys,
  FilterManager as Manager,
}
